import React, { useEffect, useMemo, useState, useContext, useCallback } from 'react';
import { connect } from 'react-redux';
import { withRouter, RouteComponentProps } from 'react-router';
import { Link } from 'react-router-dom';
import { ApplicationState, AppState, AppActionCreators } from '../../store';
import { withSnackbar, ProviderContext } from 'notistack';
import { RoleClaimsEnum } from '../../helpers/Constants';
import {
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Typography,
  Box,
  Button,
  CircularProgress
} from '@material-ui/core';
import { useTheme } from '@material-ui/core/styles';
import { ApplicationContext, AppContext } from '../../context/Contexts';
import { ITranslatorService } from '../../services/Interfaces/ITranslatorService';

interface AcceptConnectLegalDocumentsGuardProps {
  appState: AppState;
  children: React.ReactNode;
  SetUser: (enqueueSnackbar: any) => void;
}

type Props = AcceptConnectLegalDocumentsGuardProps & ProviderContext & RouteComponentProps;

const EXCLUDED_PATHS = ['/terms-and-conditions', '/privacy-policy', '/pdf-reader'];

const AcceptConnectLegalDocumentsGuard: React.FC<Props> = (props) => {
  const theme = useTheme();
  const appContext = useContext(ApplicationContext);
  const translatorService: ITranslatorService = (appContext as AppContext).translatorService;
  const legalDocumentService = (appContext as AppContext).legalDocumentService;

  const linkStyle = useMemo(
    () => ({
      cursor: 'pointer' as const,
      textDecoration: 'none' as const,
      color: theme.palette.primary.main
    }),
    [theme]
  );

  const [open, setOpen] = useState(false);
  const [isAccepting, setIsAccepting] = useState(false);

  const user = props.appState.appUser;

  const isAdmin = useMemo(() => {
    const role = props.appState?.user?.profile?.role;
    if (!role) return false;
    if (Array.isArray(role)) return role.includes(RoleClaimsEnum.Admin);
    return role === RoleClaimsEnum.Admin;
  }, [props.appState]);

  const needsToAcceptTerms = useMemo(() => {
    if (isAdmin || !user) return false;
    return !user.acceptedConnectTermsAndConditions;
  }, [user, isAdmin]);

  const needsToAcceptPrivacy = useMemo(() => {
    if (isAdmin || !user) return false;
    return !user.acceptedConnectPrivacyPolicy;
  }, [user, isAdmin]);

  const isOnExcludedPage = useMemo(() => {
    const pathname = props.location?.pathname || '';
    return EXCLUDED_PATHS.some((p) => pathname.startsWith(p));
  }, [props.location]);

  const shouldShowDialog = useMemo(() => {
    if (isOnExcludedPage) return false;
    return needsToAcceptTerms || needsToAcceptPrivacy;
  }, [needsToAcceptTerms, needsToAcceptPrivacy, isOnExcludedPage]);

  useEffect(() => {
    setOpen(shouldShowDialog);
  }, [shouldShowDialog]);

  const handleAccept = useCallback(async () => {
    if (!user?.id) return;

    const payload = {
      userId: user.id,
      acceptedConnectTermsAndConditions: true,
      acceptedConnectPrivacyPolicy: true
    };

    setIsAccepting(true);
    try {
      await legalDocumentService.AcceptLatestsLegalDocuments(payload);

      props.enqueueSnackbar(
        translatorService.Tranlate(
          'FLEET_LEGAL_DOCUMENTS_ACCEPTED_SUCCESS',
          'Latests documents accepted successfully'
        ),
        { variant: 'success' }
      );

      props.SetUser(props.enqueueSnackbar);
      setOpen(false);
    } catch {
      props.enqueueSnackbar(
        translatorService.Tranlate('FLEET_ERROR_MESSAGE', 'An error occurred'),
        { variant: 'error' }
      );
    } finally {
      setIsAccepting(false);
    }
  }, [user, legalDocumentService, props, translatorService]);

  return (
    <React.Fragment>
      {props.children}
      <Dialog
        open={open}
        onClose={(_e: any, reason: string) => {
          if (reason === 'backdropClick' || reason === 'escapeKeyDown') return;
        }}
        disableEscapeKeyDown
        disableBackdropClick
        maxWidth="sm"
        fullWidth
        style={{ zIndex: 1400 }}
      >
        <DialogTitle style={{ textAlign: 'center' }}>
          {translatorService.Tranlate(
            'FLEET_LEGAL_DOCUMENTS_ACCEPTANCE_REQUIRED_TITLE',
            'To continue, please accept:'
          )}
        </DialogTitle>
        <DialogContent>
          <Box display="flex" flexDirection="column" style={{ gap: 4 }}>
            {needsToAcceptTerms && (
              <Typography style={{ textAlign: 'center' }}>
                <Typography
                  component={Link}
                  to="/terms-and-conditions"
                  target="_blank"
                  style={linkStyle}
                >
                  {translatorService.Tranlate(
                    'FLEET_ENROLL_TERMS_AND_CONDITIONS_TEXT',
                    'Terms and Conditions'
                  )}
                </Typography>
              </Typography>
            )}

            {needsToAcceptPrivacy && (
              <Typography style={{ textAlign: 'center' }}>
                <Typography component={Link} to="/privacy-policy" target="_blank" style={linkStyle}>
                  {translatorService.Tranlate('FLEET_ENROLL_PRIVACY_POLICY_TEXT', 'Privacy Policy')}
                </Typography>
              </Typography>
            )}
          </Box>
        </DialogContent>
        <DialogActions style={{ justifyContent: 'center', paddingBottom: 16 }}>
          <Button
            variant="contained"
            color="secondary"
            onClick={handleAccept}
            size="large"
            disabled={isAccepting}
            style={{ minWidth: 150 }}
          >
            {isAccepting ? (
              <CircularProgress size={20} color="inherit" />
            ) : (
              translatorService.Tranlate('FLEET_ACCEPT_BUTTON_TEXT', 'Accepta')
            )}
          </Button>
        </DialogActions>
      </Dialog>
    </React.Fragment>
  );
};

const mapDispatchToProps = (dispatch: any) => ({
  SetUser: (enqueueSnackbar: any) => dispatch(AppActionCreators.SetUser(enqueueSnackbar))
});

export default withRouter(
  connect(
    (state: ApplicationState) => ({
      appState: state.app
    }),
    mapDispatchToProps
  )(withSnackbar(AcceptConnectLegalDocumentsGuard as any)) as any
);
